import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {Search,MapPin,Calendar,Users,Star,Shield,Clock,ArrowRight,ChevronDown,Zap,Trees,Waves,Anchor,Building2,Mountain} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from "../components/Navbar";
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { PaymentLogosBar } from '../components/PaymentBadges';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const today = new Date().toISOString().split('T')[0];
const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];

function AssetCardSkeleton() {
  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
      <Skeleton className="h-64 w-full rounded-none" />
      <div className="p-6 space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-16" />
      </div>
    </div>
  );
}

export default function Homepage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [searchType, setSearchType] = useState('');
  const [searchZone, setSearchZone] = useState('');
  const [searchFrom, setSearchFrom] = useState(today);
  const [searchTo, setSearchTo] = useState(tomorrow);

  useEffect(() => {
    if (!authLoading && user?.role === 'admin') navigate('/admin/dashboard');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/assets?limit=6`)
      .then(res => res.json())
      .then(data => { setAssets(Array.isArray(data) ? data.slice(0, 6) : []); setLoadingAssets(false); })
      .catch(() => setLoadingAssets(false));

    fetch(`${BACKEND_URL}/api/categories`)
      .then(res => res.json())
      .then(setCategories)
      .catch(console.error);
  }, []);

  const categoryIcons = {
    jet_ski: '🚤',
    atv: '🏎️',
    boat: '⛵',
    motorcycle: '🏍️',
    scooter: '🛵'
  };
  const catImg = (type) => `${process.env.PUBLIC_URL}/images/categories/${type}.jpg`;
  const KNOWN_CATS = ['jet_ski', 'atv', 'boat', 'motorcycle', 'scooter', 'car', 'bicycle', 'horse'];
  const catLabels = {
    jet_ski: t('providersLanding.catJetSki', 'Jet Skis'),
    boat: t('providersLanding.catBoat', 'Lanchas'),
    atv: t('providersLanding.catAtv', 'ATVs / Buggies'),
    motorcycle: t('providersLanding.catMotorcycle', 'Motocicletas'),
    scooter: t('providersLanding.catScooter', 'Scooters'),
    car: t('homepage.catCar', 'Coches'),
    bicycle: t('homepage.catBicycle', 'Bicicletas'),
    horse: t('homepage.catHorse', 'Caballos'),
  };
  const catLabel = (cat) => catLabels[cat.type] || cat.name || cat.type.replace(/_/g, ' ');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-40 pb-20">
        
        <img
          src={`${process.env.PUBLIC_URL}/Fondo.jpg`}
          alt="Fondo RedMobility"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-[center_35%]"
        />
        
        {/* AQUI ESTÁ EL NUEVO DEGRADADO: Más intenso en la izquierda (from-[#0a192f]) y se desvanece a la derecha */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a192f]/75 via-[#0a192f]/50 md:via-[#0f172a]/40 to-transparent" />

        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl w-full">
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-5xl text-left mt-20 md:mt-24"
          >
    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight max-w-4xl mb-6 break-words">
  <span className="tropical-text">
    {t('homepage.heroTitle1')}
  </span>
  <br />
  <span className="text-white">
    {t('homepage.heroTitle2')}
  </span>
</h1>
     <p className="text-lg md:text-xl mb-10 text-gray-100 max-w-2xl drop-shadow-md font-normal tracking-wide">
              {t('homepage.heroLead', 'Proveedores certificados. Confirmación rápida y segura. Jet skis, ATVs, lanchas y más.')}
            </p>

            <form
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                const params = new URLSearchParams();
                if (searchType) params.set('type', searchType);
                if (searchZone) params.set('zone', searchZone);
                if (searchFrom) {
                  params.set('start_datetime', searchFrom + 'T00:00');
                  if (searchTo && searchTo > searchFrom) {
                    const diffH = Math.max(
                      1,
                      Math.round((new Date(searchTo) - new Date(searchFrom)) / 3600000)
                    );
                    params.set('end_hours', diffH);
                  }
                }
                navigate(`/search?${params.toString()}`);
              }}
              className="bg-white rounded-3xl md:rounded-[2.5rem] p-2 md:p-2 shadow-[0_20px_60px_rgba(0,0,0,0.25)] w-full max-w-5xl relative z-10"
            >
              <div className="flex flex-col lg:flex-row items-center divide-y lg:divide-y-0 lg:divide-x divide-gray-200">

                <div className="flex-[1.5] w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 lg:rounded-l-[2rem] transition-colors cursor-pointer group relative min-w-0">
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <Search className="w-5 h-5 text-gray-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">{t('homepage.vehicle', 'Vehículo')}</p>
                      <select
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                        className="bg-transparent border-none outline-none w-full text-gray-900 font-medium appearance-none cursor-pointer focus:ring-0 p-0 pr-6 text-[15px]"
                      >
                        <option value="">{t('homepage.searchPlaceholderVehicle', '¿Qué buscas?')}</option>
                        {(categories.length > 0
                          ? categories
                          : KNOWN_CATS.map(type => ({ type, name: catLabels[type], icon: '' }))
                        ).map(c => (
                          <option key={c.type} value={c.type}>{catLabels[c.type] || c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-4 pointer-events-none" />
                </div>

                <div className="flex-[1.5] w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group relative min-w-0">
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <MapPin className="w-5 h-5 text-gray-700 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">{t('homepage.zone', 'Zona')}</p>
                      <select
                        value={searchZone}
                        onChange={(e) => setSearchZone(e.target.value)}
                        className="bg-transparent border-none outline-none w-full text-gray-900 font-medium appearance-none cursor-pointer focus:ring-0 p-0 pr-6 text-[15px]"
                      >
                        <option value="">{t('homepage.allZones', 'Todas las zonas')}</option>
                        <option>Punta Cana</option>
                        <option>Bávaro</option>
                        <option>Cap Cana</option>
                        <option>Santo Domingo</option>
                        <option>Samaná</option>
                        <option>Puerto Plata</option>
                        <option>Cabarete</option>
                        <option>Sosúa</option>
                        <option>La Romana</option>
                        <option>Bayahíbe</option>
                        <option>Jarabacoa</option>
                        <option>Las Galeras</option>
                        <option>Santiago</option>
                      </select>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-4 pointer-events-none" />
                </div>

                <div className="flex-[0.9] w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group">
                  
                  <div className="flex-1 w-full">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">
                      {t('homepage.fromShort', 'Desde')}
                    </p>
          <input
  type="date"
  value={searchFrom}
  min={today}
  onChange={(e) => setSearchFrom(e.target.value)}
  className="bg-transparent border-none outline-none w-full text-[#134168] font-bold text-base focus:ring-0 p-0 cursor-pointer"
/>
                  </div>
                </div>

                <div className="flex-[0.9] w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group">
                  
                  <div className="flex-1 w-full">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5">
                      {t('homepage.toShort', 'Hasta')}
                    </p>
                  <input
  type="date"
  value={searchTo}
  min={searchFrom || today}
  onChange={(e) => setSearchTo(e.target.value)}
  className="bg-transparent border-none outline-none w-full text-[#134168] font-bold text-base focus:ring-0 p-0 cursor-pointer"
/>
                  </div>
                </div>

                <div className="w-full lg:w-auto p-2">
                  <button
                    type="submit"
                    className="w-full lg:w-max px-8 h-[56px] flex items-center justify-center gap-2 bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white lg:rounded-[2rem] rounded-xl font-bold text-[15px] hover:scale-[1.02] transition-transform shadow-md"
                  >
                    {t('homepage.searchAvailability', 'Buscar disponibilidad')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

              </div>
            </form>

            <div className="flex flex-wrap items-center gap-3 mt-8 text-sm">
              <div className="flex items-center gap-2 text-white bg-white/10 border border-white/20 backdrop-blur-md px-4 py-2.5 rounded-full font-medium">
                <Shield className="w-4 h-4 text-[#36C0C7] shrink-0" />
                <span>{t('homepage.trustVerifiedShort', 'Proveedores verificados')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-white/10 border border-white/20 backdrop-blur-md px-4 py-2.5 rounded-full font-medium">
                <Star className="w-4 h-4 text-[#36C0C7] shrink-0" />
                <span>{t('homepage.trustRatingShort', '4.9★ Promedio')}</span>
              </div>
              <div className="flex items-center gap-2 text-white bg-white/10 border border-white/20 backdrop-blur-md px-4 py-2.5 rounded-full font-medium">
                <Zap className="w-4 h-4 text-[#36C0C7] shrink-0" />
                <span>{t('homepage.trustInstant', 'Confirmación inmediata')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
<div className="bg-white border-b border-gray-100 py-5 overflow-hidden shadow-sm">
  <div className="flex animate-marquee whitespace-nowrap">

    <div className="flex items-center gap-12 px-8">

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Trees className="w-4 h-4 text-gray-500" />
        PUNTA CANA
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Waves className="w-4 h-4 text-gray-500" />
        BÁVARO
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Anchor className="w-4 h-4 text-gray-500" />
        CAP CANA
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Anchor className="w-4 h-4 text-gray-500" />
        BAYAHÍBE
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Building2 className="w-4 h-4 text-gray-500" />
        SANTO DOMINGO
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Mountain className="w-4 h-4 text-gray-500" />
        PUERTO PLATA
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Waves className="w-4 h-4 text-gray-500" />
        SAMANÁ
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Trees className="w-4 h-4 text-gray-500" />
        LAS GALERAS
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Mountain className="w-4 h-4 text-gray-500" />
        JARABACOA
      </span>

    </div>

    <div className="flex items-center gap-12 px-8">

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Trees className="w-4 h-4 text-gray-500" />
        PUNTA CANA
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Waves className="w-4 h-4 text-gray-500" />
        BÁVARO
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Anchor className="w-4 h-4 text-gray-500" />
        CAP CANA
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Anchor className="w-4 h-4 text-gray-500" />
        BAYAHÍBE
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Building2 className="w-4 h-4 text-gray-500" />
        SANTO DOMINGO
      </span>

      <span className="flex items-center gap-2 text-gray-500 font-semibold text-xs uppercase">
        <Mountain className="w-4 h-4 text-gray-500" />
        PUERTO PLATA
      </span>

    </div>

  </div>
</div>
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-heading font-bold text-[#111827] mb-8"
          >
            {t('homepage.exploreByCategory')}
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(categories.filter(cat => cat.count > 0).length > 0
              ? categories.filter(cat => cat.count > 0)
              : KNOWN_CATS.map(type => ({ type, name: null, count: null }))
            ).map((cat, idx) => (
              <motion.div
                key={cat.type}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-2xl overflow-hidden cursor-pointer border border-gray-100 hover:border-[#134168] hover:shadow-xl transition-all"
                onClick={() => navigate(`/search?type=${cat.type}`)}
              >
                <div className="relative h-32 overflow-hidden">
                  {KNOWN_CATS.includes(cat.type) ? (
                    <img
                      src={catImg(cat.type)}
                      alt={cat.name || cat.type}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl bg-gradient-to-br from-[#36C0C7] to-[#134168]">
                      {categoryIcons[cat.type] || '🚀'}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                  <h3 className="absolute bottom-2 left-3 right-3 text-white font-bold drop-shadow-md">{catLabel(cat)}</h3>
                </div>
                {cat.count != null && (
                  <div className="p-3 text-center">
                    <p className="text-sm text-gray-500">{cat.count} {t('homepage.availableLabel')}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => navigate('/search')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#134168]/30 text-[#134168] font-bold hover:bg-[#134168] hover:text-white transition-all group"
            >
              {t('homepage.seeAllCategories', 'Ver todas las categorías')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {(loadingAssets || assets.length > 0) && (
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-heading font-bold text-[#111827]"
            >
              {t('homepage.featured')}
            </motion.h2>
            <button
              onClick={() => navigate('/search')}
              className="text-[#134168] font-bold hover:underline"
            >
              {t('homepage.viewAllArrow')}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {loadingAssets
              ? Array.from({ length: 6 }).map((_, i) => <AssetCardSkeleton key={i} />)
              : assets.map(asset => (
              <motion.div
                key={asset.asset_id}
                whileHover={{ y: -8 }}
                className="asset-card bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100"
              >
              <Link to={`/listing/${asset.asset_id}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#134168] rounded-3xl">
                <div className="relative h-64">
                  <img
                    src={asset.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                    alt={asset.name}
                    width="400"
                    height="256"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700 capitalize">
                      {asset.type.replace('_', ' ')}
                    </span>
                    <span className="text-xs text-gray-500">• {asset.location_zone}</span>
                  </div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{asset.name}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    {asset.total_rentals > 0 ? (
                      <>
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" aria-hidden="true" />
                        <span className="font-semibold text-sm">{asset.rating}</span>
                        <span className="text-xs text-gray-500">({asset.total_rentals} {t('homepage.rentalsCount')})</span>
                      </>
                    ) : (
                      <span className="text-xs text-[#134168] font-medium">{t('homepage.newOnPlatform')}</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#134168]">{formatDOP(asset.price_per_day)}</span>
                    <span className="text-sm text-gray-500">{t('homepage.perDayShort')}</span>
                  </div>
                </div>
              </Link>
              </motion.div>
            ))}
          </div>

        </div>
      </section>
      )}

      <section className="py-24 bg-gradient-to-b from-white to-slate-50 mb-20 md:mb-0">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-heading font-bold text-center text-[#111827] mb-12"
          >
            {t('homepage.howItWorksTitle')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
  {[
    { icon: Search, title: t('homepage.step1Title2'), desc: t('homepage.step1Desc2') },
    { icon: Calendar, title: t('homepage.step2Title2'), desc: t('homepage.step2Desc2') },
    { icon: Shield, title: t('homepage.step3Title2'), desc: t('homepage.step3Desc2') },
    { icon: Users, title: t('homepage.step4Title2'), desc: t('homepage.step4Desc2') }
  ].map((step, idx) => (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.4, delay: idx * 0.1 }}
      whileHover={{ y: -8 }}
      className="bg-white rounded-3xl p-8 text-center shadow-lg border border-gray-100 hover:border-[#36C0C7] hover:shadow-2xl transition-all"
    >
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#36C0C7] to-[#134168] flex items-center justify-center mx-auto mb-6 shadow-lg">
        <step.icon className="w-8 h-8 text-white" />
      </div>

      <div className="text-[#36C0C7] font-black text-sm mb-2">
        0{idx + 1}
      </div>

      <h3 className="font-bold text-xl text-[#111827] mb-3">
        {step.title}
      </h3>

      <p className="text-gray-500 leading-relaxed">
        {step.desc}
      </p>
    </motion.div>
  ))}
</div>
        </div>
      </section>

      {/* Sección pago seguro — requisito Azul: logos Visa/MC y 3D Secure en página principal */}
      <section className="bg-white border-t border-gray-100 py-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
            {t('homepage.paymentSecure', 'Pagos 100% seguros con autenticación 3D Secure')}
          </p>
          <PaymentLogosBar show3DS={true} className="justify-center" />
          <p className="text-xs text-gray-400 mt-3">
            <Link to="/seguridad" className="hover:text-[#134168] transition-colors underline">
              {t('homepage.securityPolicyLink', 'Ver política de seguridad de datos')}
            </Link>
          </p>
        </div>
      </section>

    </div>
  );
}